import {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getFilteredRowModel,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
  ColumnResizeMode,
} from '@tanstack/react-table';
import { ColumnType } from '@projectflow/shared';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { GridHeaderRow } from './GridHeader';
import { GridRow } from './GridRow';
import { GridCell } from './GridCell';
import { Loader2 } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';

// Types
export interface DataGridColumn {
  id: string;
  name: string;
  columnType: ColumnType;
  width: number;
  isHidden: boolean;
  isPrimary: boolean;
  options?: Record<string, unknown>;
}

export interface DataGridTask {
  id: string;
  rowNumber: number;
  outlineLevel: number;
  isSummary: boolean;
  isExpanded: boolean;
  name: string;
  parentId?: string | null;
  customFields?: Record<string, unknown>;
  [key: string]: unknown;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface DataGridProps {
  data: DataGridTask[];
  columns: DataGridColumn[];
  isLoading?: boolean;
  onCellChange?: (taskId: string, columnId: string, value: unknown, oldValue: unknown) => void;
  onTaskChange?: (taskId: string, field: string, value: unknown) => void;
  onTaskCreate?: (afterTaskId?: string) => void;
  onTaskDelete?: (taskId: string) => void;
  onTaskIndent?: (taskId: string) => void;
  onTaskOutdent?: (taskId: string) => void;
  onTaskExpand?: (taskId: string) => void;
  onTaskReorder?: (taskId: string, targetTaskId: string, position: 'before' | 'after') => void;
  onColumnResize?: (columnId: string, width: number) => void;
  onColumnReorder?: (columnId: string, newIndex: number) => void;
  onColumnAdd?: (afterColumnId?: string) => void;
  onColumnDelete?: (columnId: string) => void;
  onColumnRename?: (columnId: string, name: string) => void;
  onColumnHide?: (columnId: string) => void;
  availableContacts?: Contact[];
  className?: string;
}

const ROW_HEIGHT = 36;

export function DataGrid({
  data: tasks,
  columns,
  isLoading = false,
  onCellChange,
  onTaskChange,
  onTaskCreate,
  onTaskDelete,
  onTaskIndent,
  onTaskOutdent,
  onTaskExpand,
  onTaskReorder,
  onColumnResize,
  // onColumnReorder — drag infrastructure added to headers, callback wired when SheetView provides it
  onColumnAdd,
  onColumnDelete,
  onColumnRename,
  onColumnHide,
  availableContacts = [],
  className,
}: DataGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; taskId: string } | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dropTargetTaskId, setDropTargetTaskId] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{
    rowId: string;
    columnId: string;
  } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{
    rowIndex: number;
    columnIndex: number;
  } | null>(null);

  const queryClient = useQueryClient();

  // Right-click context menu — capture phase to intercept before inputs
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const row = target.closest('[role="row"]');
      if (row) {
        e.preventDefault();
        e.stopPropagation();
        // Find the task ID from the row's data
        const rowIndex = Array.from(container.querySelectorAll('[role="row"]')).indexOf(row);
        const task = tasks[rowIndex];
        if (task) {
          setContextMenu({ x: e.clientX, y: e.clientY, taskId: task.id });
        }
      }
    };
    container.addEventListener('contextmenu', handler, true);
    return () => container.removeEventListener('contextmenu', handler, true);
  }, [tasks]);

  // Close context menu on click outside or scroll
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', close);
    };
  }, [contextMenu]);

  // Filter visible columns
  const visibleColumns = useMemo(
    () => columns.filter((col) => !col.isHidden),
    [columns]
  );

  // Available tasks for predecessor picker
  const availableTasks = useMemo(
    () =>
      tasks.map((t) => ({
        id: t.id,
        rowNumber: t.rowNumber,
        name: t.name,
      })),
    [tasks]
  );

  const getTaskCellValue = useCallback(
    (task: DataGridTask, column: DataGridColumn) => {
      if (column.isPrimary) {
        return task.name;
      }

      // Check customFields first
      const customFields = (task.customFields as Record<string, unknown> | undefined) ?? {};
      const customValue = customFields[column.id];
      if (customValue !== undefined && customValue !== null) {
        return customValue;
      }

      // Fallback: check built-in task fields by column name
      const nameMap: Record<string, string> = {
        'start date': 'startDate',
        'finish date': 'finishDate',
        'end date': 'finishDate',
        'duration': 'duration',
        '% complete': 'percentComplete',
        'status': 'status',
      };
      const builtInKey = nameMap[column.name.toLowerCase()];
      if (builtInKey) {
        return task[builtInKey];
      }

      return customValue;
    },
    []
  );

  const serializeCellValue = useCallback((value: unknown) => {
    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map((item) => String(item)).join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }, []);

  const parseClipboardValue = useCallback(
    (text: string, columnType: ColumnType) => {
      const trimmed = text.trim();

      if (trimmed.length === 0) {
        return '';
      }

      switch (columnType) {
        case ColumnType.NUMBER:
        case ColumnType.CURRENCY:
        case ColumnType.PERCENT:
        case ColumnType.DURATION: {
          const parsed = Number(trimmed);
          return Number.isNaN(parsed) ? text : parsed;
        }
        case ColumnType.CHECKBOX:
          return ['true', '1', 'yes', 'y'].includes(trimmed.toLowerCase());
        case ColumnType.DROPDOWN_MULTI:
          return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
        default:
          return text;
      }
    },
    []
  );

  // Generate TanStack Table column definitions from sheet columns
  const tableColumns: ColumnDef<DataGridTask>[] = useMemo(() => {
    return visibleColumns.map((col): ColumnDef<DataGridTask> => ({
      id: col.id,
      accessorFn: (row) => {
        if (col.isPrimary) return row.name;
        const cf = (row.customFields as Record<string, unknown> | undefined) ?? {};
        const val = cf[col.id];
        if (val !== undefined && val !== null) return val;
        // Fallback to built-in fields
        const nameMap: Record<string, string> = {
          'start date': 'startDate', 'finish date': 'finishDate',
          'duration': 'duration', '% complete': 'percentComplete', 'status': 'status',
        };
        const builtIn = nameMap[col.name.toLowerCase()];
        return builtIn ? row[builtIn] : val;
      },
      header: col.name,
      size: col.width,
      minSize: 50,
      maxSize: 500,
      cell: ({ row, column }) => {
        const taskId = row.original.id;
        const columnId = column.id;
        const isEditing =
          editingCell?.rowId === taskId && editingCell?.columnId === columnId;
        const isSelected =
          selectedCell?.rowIndex === row.index &&
          selectedCell?.columnIndex === visibleColumns.findIndex(
            (c) => c.id === columnId
          );

        // Get value from task — check customFields first, then built-in fields
        let value: unknown;
        if (col.isPrimary) {
          value = row.original.name;
        } else {
          const customFields = (row.original.customFields as Record<string, unknown> | undefined) ?? {};
          value = customFields[col.id];
          // Fallback to built-in task fields by column name
          if (value === undefined || value === null) {
            const nameMap: Record<string, string> = {
              'start date': 'startDate', 'finish date': 'finishDate', 'end date': 'finishDate',
              'duration': 'duration', '% complete': 'percentComplete', 'status': 'status',
            };
            const builtInKey = nameMap[col.name.toLowerCase()];
            if (builtInKey && row.original[builtInKey] !== undefined) {
              value = row.original[builtInKey];
            }
          }
        }

        return (
          <GridCell
            value={value}
            columnType={col.columnType}
            columnOptions={col.options as any}
            onChange={(newValue) => {
              const fieldName = col.isPrimary ? 'name' : `customFields.${col.id}`;

              // Optimistic update — immediately update cache
              const queryCache = queryClient.getQueryCache();
              const matchQuery = queryCache.findAll({ queryKey: ['tasks'] })[0];
              if (matchQuery) {
                const cachedTasks = queryClient.getQueryData<DataGridTask[]>(matchQuery.queryKey);
                if (cachedTasks) {
                  queryClient.setQueryData(
                    matchQuery.queryKey,
                    cachedTasks.map((t: DataGridTask) => {
                      if (t.id !== taskId) return t;
                      if (fieldName.startsWith('customFields.')) {
                        const colId = fieldName.replace('customFields.', '');
                        const cf = { ...(t.customFields as Record<string, unknown> || {}) };
                        cf[colId] = newValue;
                        return { ...t, customFields: cf };
                      }
                      return { ...t, [fieldName]: newValue };
                    }),
                  );
                }
              }

              // Send to API — direct call as backup, plus onCellChange for undo/redo
              if (!col.isPrimary) {
                // Direct API call for custom field columns (dates, status, etc.)
                const sendValue = newValue instanceof Date ? newValue.toISOString() : newValue;
                api.patch(`/tasks/${taskId}/cells/${col.id}`, { value: sendValue }).catch((err) => {
                  console.error('Failed to save cell:', err);
                  queryClient.invalidateQueries({ queryKey: ['tasks'] });
                });

                // Sync dependencies when predecessor column changes
                if (col.columnType === ColumnType.PREDECESSOR && Array.isArray(newValue)) {
                  const predecessors = newValue as Array<{ taskId: string; type: string; lag: number }>;
                  // Fetch existing dependencies for this task
                  api.get(`/tasks/${taskId}/dependencies`).then((res: any) => {
                    const data = res.data || {};
                    const existing = data.predecessors || [];
                    // Delete removed dependencies
                    for (const dep of existing) {
                      if (!predecessors.find((p: any) => p.taskId === dep.predecessorId)) {
                        api.delete(`/dependencies/${dep.id}`).catch(() => {});
                      }
                    }
                    // Create new dependencies
                    for (const pred of predecessors) {
                      if (!existing.find((d: any) => d.predecessorId === pred.taskId)) {
                        const body = {
                          predecessorId: pred.taskId,
                          dependencyType: (pred.type || 'FS').toLowerCase(),
                          lagDays: pred.lag || 0,
                        };
                        console.log('[Dep] Creating:', body, 'for successor:', taskId);
                        api.post(`/tasks/${taskId}/dependencies`, body).catch((err: any) => console.error('Failed to create dependency:', err));
                      }
                    }
                    // Invalidate dependencies cache
                    queryClient.invalidateQueries({ queryKey: ['dependencies'] });
                  }).catch(() => {});
                }
              }
              if (onCellChange) {
                onCellChange(taskId, fieldName, newValue, value);
              } else {
                onTaskChange?.(taskId, fieldName, newValue);
              }
            }}
            isEditing={isEditing}
            onStartEdit={() => setEditingCell({ rowId: taskId, columnId })}
            onEndEdit={() => setEditingCell(null)}
            isSelected={isSelected}
            isPrimary={col.isPrimary}
            availableContacts={availableContacts}
            availableTasks={availableTasks}
            currentTaskId={taskId}
          />
        );
      },
    }));
  }, [
    visibleColumns,
    editingCell,
    selectedCell,
    onCellChange,
    onTaskChange,
    availableContacts,
    availableTasks,
  ]);

  // Create table instance
  const [grouping, setGrouping] = useState<string[]>([]);

  const table = useReactTable({
    data: tasks,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      grouping,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onGroupingChange: setGrouping,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    columnResizeMode: 'onChange' as ColumnResizeMode,
    enableRowSelection: true,
    enableGrouping: true,
    getRowId: (row) => row.id,
  });

  // Get rows from table
  const { rows } = table.getRowModel();

  // Virtual row renderer for performance with large datasets
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      if (isCtrlOrCmd && e.key.toLowerCase() === 'c' && selectedCell) {
        e.preventDefault();
        const row = rows[selectedCell.rowIndex]?.original;
        const column = visibleColumns[selectedCell.columnIndex];
        if (!row || !column) {
          return;
        }

        const rawValue = getTaskCellValue(row, column);
        const clipboardText = serializeCellValue(rawValue);
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(clipboardText).catch(() => {});
        }
        return;
      }

      if (isCtrlOrCmd && e.key.toLowerCase() === 'v' && selectedCell && !editingCell) {
        e.preventDefault();
        const row = rows[selectedCell.rowIndex]?.original;
        const column = visibleColumns[selectedCell.columnIndex];

        if (!row || !column || !navigator.clipboard?.readText) {
          return;
        }

        navigator.clipboard
          .readText()
          .then((clipboardText) => {
            const oldValue = getTaskCellValue(row, column);
            const newValue = parseClipboardValue(clipboardText, column.columnType);
            const fieldName = column.isPrimary ? 'name' : `customFields.${column.id}`;

            if (onCellChange) {
              onCellChange(row.id, fieldName, newValue, oldValue);
            } else {
              onTaskChange?.(row.id, fieldName, newValue);
            }
          })
          .catch(() => {});
        return;
      }

      if (!selectedCell) return;

      const { rowIndex, columnIndex } = selectedCell;
      const maxRow = rows.length - 1;
      const maxCol = visibleColumns.length - 1;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (rowIndex > 0) {
            setSelectedCell({ rowIndex: rowIndex - 1, columnIndex });
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (rowIndex < maxRow) {
            setSelectedCell({ rowIndex: rowIndex + 1, columnIndex });
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (columnIndex > 0) {
            setSelectedCell({ rowIndex, columnIndex: columnIndex - 1 });
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (columnIndex < maxCol) {
            setSelectedCell({ rowIndex, columnIndex: columnIndex + 1 });
          }
          break;
        case 'Tab':
          e.preventDefault();
          if (e.shiftKey) {
            // Move left
            if (columnIndex > 0) {
              setSelectedCell({ rowIndex, columnIndex: columnIndex - 1 });
            } else if (rowIndex > 0) {
              setSelectedCell({ rowIndex: rowIndex - 1, columnIndex: maxCol });
            }
          } else {
            // Move right
            if (columnIndex < maxCol) {
              setSelectedCell({ rowIndex, columnIndex: columnIndex + 1 });
            } else if (rowIndex < maxRow) {
              setSelectedCell({ rowIndex: rowIndex + 1, columnIndex: 0 });
            }
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (!editingCell) {
            const task = rows[rowIndex]?.original;
            const column = visibleColumns[columnIndex];
            if (task && column) {
              setEditingCell({ rowId: task.id, columnId: column.id });
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setEditingCell(null);
          break;
        case 'Delete':
        case 'Backspace':
          if (!editingCell && e.key === 'Delete') {
            const task = rows[rowIndex]?.original;
            if (task) {
              onTaskDelete?.(task.id);
            }
          }
          break;
      }
    },
    [
      selectedCell,
      rows,
      visibleColumns,
      editingCell,
      onTaskDelete,
      getTaskCellValue,
      parseClipboardValue,
      serializeCellValue,
      onCellChange,
      onTaskChange,
    ]
  );

  // Handle cell click
  const handleCellClick = useCallback(
    (rowIndex: number, columnIndex: number) => {
      setSelectedCell({ rowIndex, columnIndex });
    },
    []
  );

  // Convert sort state for header component
  const sortState = useMemo(() => {
    const state: Record<string, 'asc' | 'desc'> = {};
    sorting.forEach((sort) => {
      state[sort.id] = sort.desc ? 'desc' : 'asc';
    });
    return state;
  }, [sorting]);

  // Column resize handler
  const handleColumnResize = useCallback(
    (columnId: string, width: number) => {
      onColumnResize?.(columnId, width);
    },
    [onColumnResize]
  );

  // Handle column resize end
  useEffect(() => {
    const columnSizing = table.getState().columnSizing;
    Object.entries(columnSizing).forEach(([columnId, width]) => {
      handleColumnResize(columnId, width);
    });
  }, [table.getState().columnSizing, handleColumnResize]);

  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center h-64 bg-white border rounded',
          className
        )}
      >
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center h-64 bg-white border rounded',
          className
        )}
      >
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <svg
            className="h-12 w-12"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <span className="text-sm">No tasks yet</span>
          <button
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => onTaskCreate?.()}
          >
            Add first task
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-auto bg-white border rounded',
        'focus:outline-none',
        className
      )}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="grid"
      aria-rowcount={tasks.length}
      aria-colcount={visibleColumns.length}
    >
      {/* Header */}
      <GridHeaderRow
        headers={table.getFlatHeaders()}
        onSort={(columnId, direction) => {
          if (direction) {
            setSorting([{ id: columnId, desc: direction === 'desc' }]);
          } else {
            setSorting([]);
          }
        }}
        onHide={onColumnHide}
        onRename={onColumnRename}
        onDelete={onColumnDelete}
        onInsertLeft={(columnId) => onColumnAdd?.(columnId)}
        onInsertRight={(columnId) => onColumnAdd?.(columnId)}
        onAddColumn={() => onColumnAdd?.()}
        sortState={sortState}
        filterState={{}}
      />

      {/* Rows — virtualized for performance with large datasets */}
      <div
        className="w-full relative"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          const taskId = row.original.id;
          return (
            <div
              key={row.id}
              className={cn(
                'absolute w-full',
                dropTargetTaskId === taskId && 'border-t-2 border-t-blue-500',
              )}
              style={{
                height: ROW_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              onClick={() => handleCellClick(virtualRow.index, 0)}
              onDragOver={(e) => {
                e.preventDefault();
                if (draggedTaskId && draggedTaskId !== taskId) {
                  setDropTargetTaskId(taskId);
                }
              }}
              onDragLeave={() => setDropTargetTaskId(null)}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedTaskId && draggedTaskId !== taskId) {
                  onTaskReorder?.(draggedTaskId, taskId, 'before');
                }
                setDraggedTaskId(null);
                setDropTargetTaskId(null);
              }}
            >
              <GridRow
                row={row}
                isSelected={!!rowSelection[row.id]}
                isDragging={draggedTaskId === taskId}
                onSelect={(rowId, selected) => {
                  setRowSelection((prev) => ({
                    ...prev,
                    [rowId]: selected,
                  }));
                }}
                onExpand={onTaskExpand || (() => {})}
                onIndent={onTaskIndent || (() => {})}
                onOutdent={onTaskOutdent || (() => {})}
                onDelete={onTaskDelete || (() => {})}
                onInsertAbove={(taskId) => onTaskCreate?.(taskId)}
                onInsertBelow={(taskId) => onTaskCreate?.(taskId)}
                onDragStart={(id) => setDraggedTaskId(id)}
                onDragEnd={() => { setDraggedTaskId(null); setDropTargetTaskId(null); }}
              />
            </div>
          );
        })}
      </div>

      {/* Add row button at bottom */}
      <div
        className="flex items-center h-9 border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
        onClick={() => onTaskCreate?.()}
      >
        <div className="w-6" />
        <div className="w-[50px]" />
        <div className="w-10" />
        <div className="px-4 text-sm text-gray-500">+ Add task</div>
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 min-w-[160px] overflow-hidden rounded-md border bg-white p-1 shadow-md"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-gray-100" onClick={() => { onTaskCreate?.(contextMenu.taskId); setContextMenu(null); }}>
            Insert row above
          </button>
          <button className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-gray-100" onClick={() => { onTaskCreate?.(contextMenu.taskId); setContextMenu(null); }}>
            Insert row below
          </button>
          <div className="-mx-1 my-1 h-px bg-gray-200" />
          <button className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-gray-100" onClick={() => { onTaskIndent?.(contextMenu.taskId); setContextMenu(null); }}>
            Indent
          </button>
          <button className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-gray-100" onClick={() => { onTaskOutdent?.(contextMenu.taskId); setContextMenu(null); }}>
            Outdent
          </button>
          <div className="-mx-1 my-1 h-px bg-gray-200" />
          <button className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-gray-100" onClick={() => { onTaskDelete?.(contextMenu.taskId); setContextMenu(null); }}>
            <span className="text-red-600">Delete row</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default DataGrid;
